/** Home: every device on the account, grouped by site, with what needs attention first. */
import { useMemo, useState } from "react";
import { api, type Device, type Reading, type Site } from "../api.ts";
import { loadAccount } from "../farm-data.ts";
import { ago, arrangeReadings, clock, countByStatus, deviceStatus, formatEui, formatReading, modelName, STATUS_TEXT, STATUS_TONE, type Status } from "../format.ts";
import { go } from "../route.ts";
import { Bars, Icon, Problem, SavedCopyBanner, Section, Spinner, useLoad, useNow } from "../ui.tsx";

type Filter = "all" | "attention" | Status;

export function Devices({ accountId }: { accountId: string | undefined }) {
  const now = useNow();
  const { data, error, loading, reload } = useLoad(() => loadAccount(accountId), [accountId], 60_000);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => countByStatus(data?.devices ?? [], now), [data, now]);
  const attention = useMemo(
    () =>
      (data?.devices ?? [])
        .map((d) => ({ d, s: deviceStatus(d, now) }))
        .filter((x) => x.s !== "online")
        .sort((a, b) => (a.d.lastSeenAt ?? 0) - (b.d.lastSeenAt ?? 0)),
    [data, now],
  );

  if (error && !data) return <Problem error={error} onRetry={reload} />;
  if (!data) return <Spinner />;
  if (!data.devices.length) {
    return (
      <div className="card empty">
        No devices on this account yet.
        <br />
        FMS adds them during set-up.
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const shown = data.devices.filter((d) => {
    const s = deviceStatus(d, now);
    if (filter === "attention" && s === "online") return false;
    if (filter === "offline" && s !== "offline" && s !== "never") return false;
    if ((filter === "online" || filter === "late") && s !== filter) return false;
    return !q || d.name.toLowerCase().includes(q) || d.devEui.includes(q.replace(/\s/g, "")) || modelName(d.model).toLowerCase().includes(q);
  });
  const siteName = new Map(data.sites.map((s) => [s.id, s.name]));
  const groups = new Map<string, Device[]>();
  for (const d of shown) groups.set(d.siteId, [...(groups.get(d.siteId) ?? []), d]);
  const ordered = [...groups.entries()].sort(([a], [b]) => (siteName.get(a) ?? "").localeCompare(siteName.get(b) ?? ""));

  return (
    <>
      {data.savedAt !== undefined && <SavedCopyBanner at={data.savedAt} now={now} onRetry={reload} />}
      <div className="tiles">
        <SummaryTile label="Online" tone="good" n={counts.online} total={data.devices.length} active={filter === "online"} onClick={() => setFilter(filter === "online" ? "all" : "online")} />
        <SummaryTile label="Report missed" tone="warn" n={counts.late} active={filter === "late"} onClick={() => setFilter(filter === "late" ? "all" : "late")} />
        <SummaryTile label="Offline" tone="crit" n={counts.offline + counts.never} active={filter === "offline"} onClick={() => setFilter(filter === "offline" ? "all" : "offline")} />
        <div className="tile">
          <div className="lbl">Sites</div>
          <div className="big num">{data.sites.length}</div>
          <div className="foot">{data.devices.length} devices</div>
        </div>
      </div>

      {attention.length > 0 && filter === "all" && !q && (
        <Section title="Needs attention">
          <div className="card attention">
            {attention.slice(0, 5).map(({ d, s }) => (
              <button key={d.devEui} className={`att ${s === "late" ? "" : "crit"}`} onClick={() => go({ name: "device", eui: d.devEui })}>
                <span className="bar" />
                <span className="body">
                  <h3>
                    {d.name} · {STATUS_TEXT[s]}
                  </h3>
                  <p>
                    {s === "never" ? "Nothing has been heard from this device yet." : `Last heard ${ago(d.lastSeenAt, now)}.`} {siteName.get(d.siteId) ?? ""}
                  </p>
                </span>
                <span className="chev">
                  <Icon name="chev" />
                </span>
              </button>
            ))}
            {attention.length > 5 && (
              <button className="att" onClick={() => setFilter("attention")}>
                <span className="bar" style={{ background: "var(--line)" }} />
                <span className="body">
                  <h3>Show all {attention.length}</h3>
                </span>
                <span />
              </button>
            )}
          </div>
        </Section>
      )}

      <Section
        title="Devices"
        right={
          <button className="btn ghost" onClick={reload} disabled={loading} aria-label="Refresh">
            <Icon name="refresh" size={18} /> {loading ? "Updating…" : "Refresh"}
          </button>
        }
      >
        <div className="toolbar">
          <input className="search" type="search" placeholder="Search name, model or EUI" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="chips" role="group" aria-label="Show">
            {(["all", "attention"] as const).map((f) => (
              <button key={f} className="chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {f === "all" ? "All" : "Needs attention"}
              </button>
            ))}
          </div>
        </div>
        {error ? <Problem error={error} onRetry={reload} /> : null}
        <div className="card dev-list">
          {!shown.length && <div className="empty">No devices match.</div>}
          {ordered.map(([siteId, list]) => (
            <div key={siteId}>
              <div className="site-h">
                {siteName.get(siteId) ?? "Other"} <span style={{ fontWeight: 500 }}>· {list.length}</span>
              </div>
              {list
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((d) => (
                  <DeviceRow key={d.devEui} d={d} latest={data.latest.get(d.devEui) ?? []} now={now} />
                ))}
            </div>
          ))}
        </div>
      </Section>
      <ArchivedDevices accountId={accountId} sites={data.sites} />
    </>
  );
}

/** Old devices, out of the way but not gone: open one to see its history or bring it back. */
function ArchivedDevices({ accountId, sites }: { accountId: string | undefined; sites: Site[] }) {
  const archived = useLoad(() => api.archivedDevices(accountId).catch(() => ({ devices: [] })), [accountId]);
  const [open, setOpen] = useState(false);
  const list = archived.data?.devices ?? [];
  if (!list.length) return null;
  return (
    <div className="muted small" style={{ marginTop: 8 }}>
      <button className="btn ghost" onClick={() => setOpen(!open)}>
        {open ? "Hide" : "Show"} {list.length} archived device{list.length === 1 ? "" : "s"}
      </button>
      {open && (
        <div className="card list" style={{ marginTop: 8 }}>
          {list.map((d) => (
            <button key={d.devEui} className="row" style={{ textAlign: "left", width: "100%" }} onClick={() => go({ name: "device", eui: d.devEui })}>
              <span className="dot" />
              <div>
                <div className="t">{d.name}</div>
                <div className="d">
                  {modelName(d.model)} · {sites.find((s) => s.id === d.siteId)?.name ?? ""} · archived {d.archivedAt ? clock(d.archivedAt, Date.now()) : ""}
                </div>
              </div>
              <span className="mono small">{formatEui(d.devEui)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, tone, n, total, active, onClick }: { label: string; tone: string; n: number; total?: number; active: boolean; onClick: () => void }) {
  return (
    <button className="tile" aria-pressed={active} onClick={onClick}>
      <div className="lbl">
        <span className={`dot ${tone}`} /> {label}
      </div>
      <div className="big num">
        {n}
        {total !== undefined && <small>/ {total}</small>}
      </div>
      <div className="foot">{active ? "Showing only these" : "Tap to show"}</div>
    </button>
  );
}

function DeviceRow({ d, latest, now }: { d: Device; latest: Reading[]; now: number }) {
  const status = deviceStatus(d, now);
  const { measured } = arrangeReadings(d, latest);
  const key = measured[0];
  const shown = key ? formatReading(d, key) : undefined;
  const rssi = latest.find((r) => r.port === "rssi")?.value;
  const tone = STATUS_TONE[status];
  return (
    <button className="dev" onClick={() => go({ name: "device", eui: d.devEui })}>
      <span className={`dot ${tone}`} title={STATUS_TEXT[status]} />
      <span style={{ minWidth: 0 }}>
        <div className="dev-name">{d.name}</div>
        <div className="dev-sub">
          {modelName(d.model)}
          {status !== "online" && ` · ${STATUS_TEXT[status]}`}
        </div>
      </span>
      <span className="dev-key">
        {key && shown ? (
          <>
            <span className="k">{key.label}</span>
            {shown.value}
            {shown.unit && <small>{shown.unit}</small>}
          </>
        ) : (
          <span className="k">No readings yet</span>
        )}
      </span>
      <Bars rssi={rssi} />
      <span className={`dev-when ${tone === "good" ? "" : tone}`}>{ago(d.lastSeenAt, now)}</span>
    </button>
  );
}
